import {
  ExtendedRefs,
  FloatingFocusManager,
  FloatingList,
  FloatingPortal,
  autoUpdate,
  flip,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  FloatingContext,
} from '@floating-ui/react';
import {
  DropdownIcon,
  DropdownNavigationList,
  ExternalIcon,
  IconSize,
  MainNavigationDropdownButton,
  styled,
} from '@yarmill/components';
import { NavigationLink as iNavigationLink } from '@yarmill/types';
import { useDefaultCommonSearchParams, useWindowSize } from '@yarmill/utils';
import {
  AriaAttributes,
  CSSProperties,
  MutableRefObject,
  ReactElement,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { NavItem } from './nav-item';
import { NavLinkContent } from './nav-link-conent';
import { NavigationLink } from './navigation-link';
import { DropdownContext } from './utils';
import { isActive } from './utils/is-active';

export interface DropdownLinksProps {
  readonly label: ReactElement;
  readonly icon: ReactElement;
  readonly links: iNavigationLink[];
  readonly id?: string;
  readonly isActive?: (pathname: string) => boolean;
}

const FloatingElement = styled.div`
  z-index: 4;

  :focus, :focus-visible {
    color: #4a90e2;
    outline: none;
  }
`;

interface DropdownPortalProps extends AriaAttributes {
  readonly children: ReactElement;
  readonly refs: ExtendedRefs<Element>;
  readonly floatingStyles: CSSProperties;
  readonly getFloatingProps: () => Record<string, unknown>;
  readonly elementsRef: MutableRefObject<Array<HTMLButtonElement | null>>;
  readonly isDropdownOpened: boolean;
  readonly floatingContext: FloatingContext;
}
function DropdownPortal({
  children,
  refs,
  floatingStyles,
  getFloatingProps,
  elementsRef,
  isDropdownOpened,
  floatingContext,
}: DropdownPortalProps): ReactElement | null {
  const { width = 0 } = useWindowSize();

  if (width < 768) {
    return children;
  }

  if (!isDropdownOpened) {
    return null;
  }

  return (
    <FloatingList elementsRef={elementsRef}>
      <FloatingPortal>
        <FloatingElement
          style={floatingStyles}
          {...getFloatingProps()}
          ref={refs.setFloating}
        >
          <FloatingFocusManager
            context={floatingContext}
            modal={false}
            initialFocus={1}
            returnFocus
          >
            {children}
          </FloatingFocusManager>
        </FloatingElement>
      </FloatingPortal>
    </FloatingList>
  );
}

export function DropdownLinks(props: DropdownLinksProps): ReactElement | null {
  const {
    label,
    icon,
    links,
    id = 'otherNavigationItems',
    isActive: isDropdownActive,
  } = props;
  const [isDropdownOpened, setIsDropdownOpened] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const elementsRef = useRef<Array<HTMLButtonElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isDropdownOpened,
    onOpenChange: setIsDropdownOpened,
    placement: 'bottom-start',
    middleware: [flip()],
    whileElementsMounted: autoUpdate,
  });
  const defaultSearchParams = useDefaultCommonSearchParams();
  const intl = useIntl();
  const location = useLocation();

  const closeDropdown = useCallback(() => {
    setIsDropdownOpened(false);
  }, []);

  const dismiss = useDismiss(context, {
    bubbles: true,
  });
  const click = useClick(context);
  const listNavigation = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    focusItemOnOpen: false,
    onNavigate: setActiveIndex,
  });
  const role = useRole(context, { role: 'menu' });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [dismiss, click, role, listNavigation]
  );

  if (links.length === 0) {
    return null;
  }

  if (links.length === 1) {
    const module = links[0];

    if (!module.createClickHandler) {
      return null;
    }
    return (
      <NavItem
        moduleKey={module.moduleCode}
        to={module.createClickHandler(defaultSearchParams)}
        linkText={intl.formatMessage({ id: module.label })}
        icon={module.icon}
        isLinkActive={module.isActive}
      />
    );
  }

  const hasActiveLink = isDropdownActive
    ? isDropdownActive(location.pathname)
    : links.some(link =>
        isActive(
          location.pathname,
          link.createClickHandler?.(defaultSearchParams)(location) ?? '#@#'
        )
      );

  return (
    <>
      <MainNavigationDropdownButton
        id={id}
        role="menuitem"
        aria-haspopup="true"
        aria-expanded={isDropdownOpened}
        $isActiveLink={hasActiveLink || isDropdownOpened}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <NavLinkContent linkText={label} linkIcon={icon} />
        <DropdownIcon size={IconSize.s16}>
          {isDropdownOpened ? (
            <ExternalIcon name="ChevronUp" />
          ) : (
            <ExternalIcon name="ChevronDown" />
          )}
        </DropdownIcon>
      </MainNavigationDropdownButton>
      <DropdownPortal
        refs={refs}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        elementsRef={elementsRef}
        aria-labelledby={id}
        aria-orientation="vertical"
        isDropdownOpened={isDropdownOpened}
        floatingContext={context}
      >
        <DropdownContext.Provider value={closeDropdown}>
          <DropdownNavigationList>
            {links.map((module, idx) => (
              <NavigationLink
                item={module}
                key={idx}
                getItemProps={getItemProps}
                isActive={idx === activeIndex}
              />
            ))}
          </DropdownNavigationList>
        </DropdownContext.Provider>
      </DropdownPortal>
    </>
  );
}
